import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { InferResponseType } from 'hono/client';
import { Calendar, Sparkles, X } from 'lucide-react';
import {
  PERSON_KEYS,
  PERSON_KEY_LABELS,
  type PersonKey,
} from '@everplan/shared';
import { client } from '@/lib/api';
import {
  zonedWallClockToInstant,
  instantToZonedWallClock,
  browserTimezone,
  COMMON_TIMEZONES,
} from '@/lib/timezone';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RichBody, type Mentionable } from '@/components/event-doc/RichBody';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// ─── Shared bits ─────────────────────────────────────────────────────────────

type EventDetail = InferResponseType<(typeof client.api.events)[':id']['$get'], 200>;

type ParticipantOption = InferResponseType<
  (typeof client.api.events)[':id']['participant-options']['$get'],
  200
>[number];

function TimezoneSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const browserTz = browserTimezone();
  const options = useMemo(() => {
    const set = new Set<string>([browserTz, ...COMMON_TIMEZONES]);
    return Array.from(set);
  }, [browserTz]);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Select timezone" />
      </SelectTrigger>
      <SelectContent>
        {options.map((tz) => (
          <SelectItem key={tz} value={tz}>
            {tz}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Default the event timezone to the project's venue timezone if we can guess
// it from the venue string — otherwise fall back to the browser timezone.
// The "guess" is intentionally trivial (matches a US state name to a zone);
// users can always override.
function guessTimezoneFromVenue(venueText: string | null | undefined): string | null {
  if (!venueText) return null;
  const v = venueText.toLowerCase();
  const map: Record<string, string> = {
    california: 'America/Los_Angeles', ' ca ': 'America/Los_Angeles', ', ca': 'America/Los_Angeles',
    nevada: 'America/Los_Angeles', ' nv ': 'America/Los_Angeles', ', nv': 'America/Los_Angeles',
    washington: 'America/Los_Angeles', ' wa ': 'America/Los_Angeles', ', wa': 'America/Los_Angeles',
    oregon: 'America/Los_Angeles', ' or ': 'America/Los_Angeles', ', or': 'America/Los_Angeles',
    arizona: 'America/Phoenix', ' az ': 'America/Phoenix', ', az': 'America/Phoenix',
    colorado: 'America/Denver', ' co ': 'America/Denver', ', co': 'America/Denver',
    utah: 'America/Denver', ' ut ': 'America/Denver', ', ut': 'America/Denver',
    texas: 'America/Chicago', ' tx ': 'America/Chicago', ', tx': 'America/Chicago',
    illinois: 'America/Chicago', ' il ': 'America/Chicago', ', il': 'America/Chicago',
    'new york': 'America/New_York', ' ny ': 'America/New_York', ', ny': 'America/New_York',
    florida: 'America/New_York', ' fl ': 'America/New_York', ', fl': 'America/New_York',
    massachusetts: 'America/New_York', ' ma ': 'America/New_York', ', ma': 'America/New_York',
  };
  for (const [needle, tz] of Object.entries(map)) {
    if (v.includes(needle)) return tz;
  }
  return null;
}

// ─── New blank event ─────────────────────────────────────────────────────────

export function NewBlankEventDialog({
  projectId,
  venueName,
  open,
  onOpenChange,
}: {
  projectId: string;
  venueName: string | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [timezone, setTimezone] = useState(
    () => guessTimezoneFromVenue(venueName) ?? browserTimezone(),
  );
  const [startWall, setStartWall] = useState('');
  const [endWall, setEndWall] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setLocation(venueName ?? '');
      setTimezone(guessTimezoneFromVenue(venueName) ?? browserTimezone());
      setStartWall('');
      setEndWall('');
      setError(null);
    }
  }, [open, venueName]);

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Name is required');
      if (!startWall || !endWall) throw new Error('Start and end times are required');
      const startAt = zonedWallClockToInstant(startWall, timezone);
      const endAt = zonedWallClockToInstant(endWall, timezone);
      if (endAt.getTime() <= startAt.getTime()) throw new Error('End must be after start');
      const res = await client.api.events.$post({
        json: {
          projectId,
          name: name.trim(),
          startAt: startAt.toISOString(),
          endAt: endAt.toISOString(),
          timezone,
          location: location.trim() || null,
        },
      });
      if (!res.ok) throw new Error('Failed to create event');
      return res.json();
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['events', projectId] });
      onOpenChange(false);
      // Open the new event in the URL so the timeline view focuses on it.
      const url = new URL(window.location.href);
      url.searchParams.set('event', created.id);
      window.history.pushState({}, '', url);
      // RR doesn't see history.pushState; nudge it via popstate.
      window.dispatchEvent(new PopStateEvent('popstate'));
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New event</DialogTitle>
          <DialogDescription>
            Add a distinct event to this project. Steps come next.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="event-name">Name</Label>
            <Input
              id="event-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Welcome Dinner"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="event-start">Start</Label>
              <Input
                id="event-start"
                type="datetime-local"
                value={startWall}
                onChange={(e) => {
                  setStartWall(e.target.value);
                  if (!endWall && e.target.value) {
                    // Default end to +2 hours
                    const start = new Date(e.target.value);
                    const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
                    const pad = (n: number) => String(n).padStart(2, '0');
                    setEndWall(
                      `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}T${pad(end.getHours())}:${pad(end.getMinutes())}`,
                    );
                  }
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="event-end">End</Label>
              <Input
                id="event-end"
                type="datetime-local"
                value={endWall}
                onChange={(e) => setEndWall(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="event-tz">Timezone</Label>
            <TimezoneSelect value={timezone} onChange={setTimezone} />
            <p className="text-xs text-muted-foreground">
              Times above are interpreted in this zone.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="event-location">Location (optional)</Label>
            <Input
              id="event-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Madera Kitchen, Ojai CA"
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating…' : 'Create event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit event ──────────────────────────────────────────────────────────────

export function EditEventDialog({
  event,
  open,
  onOpenChange,
}: {
  event: EventDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(event.name);
  const [location, setLocation] = useState(event.location ?? '');
  const [timezone, setTimezone] = useState(event.timezone);
  const [startWall, setStartWall] = useState(() =>
    instantToZonedWallClock(event.startAt, event.timezone),
  );
  const [endWall, setEndWall] = useState(() =>
    instantToZonedWallClock(event.endAt, event.timezone),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(event.name);
      setLocation(event.location ?? '');
      setTimezone(event.timezone);
      setStartWall(instantToZonedWallClock(event.startAt, event.timezone));
      setEndWall(instantToZonedWallClock(event.endAt, event.timezone));
      setError(null);
    }
  }, [open, event]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Name is required');
      const startAt = zonedWallClockToInstant(startWall, timezone);
      const endAt = zonedWallClockToInstant(endWall, timezone);
      if (endAt.getTime() <= startAt.getTime()) throw new Error('End must be after start');
      const res = await client.api.events[':id'].$put({
        param: { id: event.id },
        json: {
          name: name.trim(),
          location: location.trim() || null,
          timezone,
          startAt: startAt.toISOString(),
          endAt: endAt.toISOString(),
        },
      });
      if (!res.ok) throw new Error('Failed to update event');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', event.id] });
      queryClient.invalidateQueries({ queryKey: ['events', event.projectId] });
      onOpenChange(false);
    },
    onError: (err: Error) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await client.api.events[':id'].$delete({ param: { id: event.id } });
      if (!res.ok) throw new Error('Failed to delete event');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', event.projectId] });
      onOpenChange(false);
      const url = new URL(window.location.href);
      url.searchParams.delete('event');
      window.history.pushState({}, '', url);
      window.dispatchEvent(new PopStateEvent('popstate'));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit event</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-event-name">Name</Label>
            <Input id="edit-event-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-event-start">Start</Label>
              <Input
                id="edit-event-start"
                type="datetime-local"
                value={startWall}
                onChange={(e) => setStartWall(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-event-end">End</Label>
              <Input
                id="edit-event-end"
                type="datetime-local"
                value={endWall}
                onChange={(e) => setEndWall(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Timezone</Label>
            <TimezoneSelect value={timezone} onChange={setTimezone} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-event-location">Location</Label>
            <Input
              id="edit-event-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter className="justify-between sm:justify-between">
          <Button
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={() => {
              if (confirm('Delete this event and all its timeline steps? This cannot be undone.')) {
                deleteMutation.mutate();
              }
            }}
            disabled={deleteMutation.isPending}
          >
            Delete event
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => updateMutation.mutate()}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Add / edit step ─────────────────────────────────────────────────────────

interface StepDialogStep {
  id: string;
  title: string;
  category: string | null;
  startAt: string;
  endAt: string | null;
  bodyJson: unknown | null;
  bodyText: string | null;
  includeInSummary: boolean;
  participants: { id: string; projectVendorId: string | null; personKey: PersonKey | null; freeText: string | null; role: string | null }[];
}

export function StepDialog({
  event,
  step,
  open,
  onOpenChange,
}: {
  event: EventDetail;
  step: StepDialogStep | null; // null = create
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = step !== null;

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [startWall, setStartWall] = useState('');
  const [endWall, setEndWall] = useState('');
  const [hasEndTime, setHasEndTime] = useState(true);
  const [includeInSummary, setIncludeInSummary] = useState(true);
  const [bodyJson, setBodyJson] = useState<unknown | null>(null);
  const [bodyText, setBodyText] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Per-row participant editor state. `id` is a transient key when adding;
  // existing participants carry their DB id.
  type DraftParticipant = {
    key: string; // local key
    id?: string; // DB id if persisted
    kind: 'vendor' | 'person' | 'free';
    projectVendorId?: string;
    personKey?: PersonKey;
    freeText?: string;
    role?: string;
  };
  const [participants, setParticipants] = useState<DraftParticipant[]>([]);

  const { data: pvOptions } = useQuery({
    queryKey: ['event', event.id, 'participant-options'],
    queryFn: async () => {
      const res = await client.api.events[':id']['participant-options'].$get({
        param: { id: event.id },
      });
      if (!res.ok) throw new Error('Failed to load vendor options');
      return res.json();
    },
    enabled: open,
  });

  // Mention picker entries — vendors active on this event's project + the
  // four canonical person keys.
  const mentionables: Mentionable[] = useMemo(() => {
    const fromVendors: Mentionable[] = (pvOptions ?? []).map((pv: ParticipantOption) => ({
      id: `pv:${pv.id}`,
      kind: 'vendor',
      label: pv.vendor.name,
      description: pv.role ?? undefined,
    }));
    const fromPeople: Mentionable[] = PERSON_KEYS.map((k) => ({
      id: `person:${k}`,
      kind: 'person',
      label: PERSON_KEY_LABELS[k],
    }));
    return [...fromPeople, ...fromVendors];
  }, [pvOptions]);

  useEffect(() => {
    if (!open) return;
    if (step) {
      setTitle(step.title);
      setCategory(step.category ?? '');
      setStartWall(instantToZonedWallClock(step.startAt, event.timezone));
      setHasEndTime(step.endAt !== null);
      setEndWall(
        step.endAt
          ? instantToZonedWallClock(step.endAt, event.timezone)
          : instantToZonedWallClock(step.startAt, event.timezone),
      );
      setIncludeInSummary(step.includeInSummary);
      setBodyJson(step.bodyJson ?? null);
      setBodyText(step.bodyText ?? '');
      setParticipants(
        step.participants.map((p, i) => ({
          key: `existing-${i}`,
          id: p.id,
          kind: p.projectVendorId ? 'vendor' : p.personKey ? 'person' : 'free',
          projectVendorId: p.projectVendorId ?? undefined,
          personKey: p.personKey ?? undefined,
          freeText: p.freeText ?? undefined,
          role: p.role ?? undefined,
        })),
      );
    } else {
      setTitle('');
      setCategory('');
      setStartWall(instantToZonedWallClock(event.startAt, event.timezone));
      setHasEndTime(true);
      setEndWall(
        instantToZonedWallClock(
          new Date(new Date(event.startAt).getTime() + 30 * 60 * 1000),
          event.timezone,
        ),
      );
      setIncludeInSummary(true);
      setBodyJson(null);
      setBodyText('');
      setParticipants([]);
    }
    setError(null);
  }, [open, step, event]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!title.trim()) throw new Error('Title is required');
      const startAt = zonedWallClockToInstant(startWall, event.timezone);
      const endAt = hasEndTime ? zonedWallClockToInstant(endWall, event.timezone) : null;
      if (endAt && endAt.getTime() <= startAt.getTime()) {
        throw new Error('End must be after start');
      }

      let savedStepId: string;
      const stepPayload = {
        title: title.trim(),
        category: category.trim() || null,
        startAt: startAt.toISOString(),
        endAt: endAt ? endAt.toISOString() : null,
        bodyJson: bodyJson ?? null,
        bodyText: bodyText || null,
        includeInSummary,
      };
      if (isEdit && step) {
        const res = await client.api['event-steps'][':id'].$put({
          param: { id: step.id },
          json: stepPayload,
        });
        if (!res.ok) throw new Error('Failed to update step');
        savedStepId = step.id;
      } else {
        const res = await client.api['event-steps'].$post({
          json: { eventId: event.id, ...stepPayload },
        });
        if (!res.ok) throw new Error('Failed to create step');
        const created = await res.json();
        savedStepId = created.id;
      }

      // Diff participants: delete removed, insert added.
      const existingIds = new Set(step?.participants.map((p) => p.id) ?? []);
      const keptIds = new Set(participants.filter((p) => p.id).map((p) => p.id!));
      for (const id of existingIds) {
        if (!keptIds.has(id)) {
          await client.api['event-steps'].participants[':participantId'].$delete({
            param: { participantId: id },
          });
        }
      }
      for (const p of participants) {
        if (p.id) continue; // already persisted
        const payload =
          p.kind === 'vendor'
            ? { projectVendorId: p.projectVendorId!, role: p.role || null }
            : p.kind === 'person'
              ? { personKey: p.personKey!, role: p.role || null }
              : { freeText: p.freeText!, role: p.role || null };
        await client.api['event-steps'][':id'].participants.$post({
          param: { id: savedStepId },
          json: payload,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', event.id] });
      onOpenChange(false);
    },
    onError: (err: Error) => setError(err.message),
  });

  const deleteStepMutation = useMutation({
    mutationFn: async () => {
      if (!step) return;
      const res = await client.api['event-steps'][':id'].$delete({ param: { id: step.id } });
      if (!res.ok) throw new Error('Failed to delete step');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', event.id] });
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit step' : 'Add step'}</DialogTitle>
          <DialogDescription>
            Times are in {event.timezone}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="step-title">Title</Label>
            <Input
              id="step-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Florist setup"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="step-category">Category</Label>
              <Input
                id="step-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. CEREMONY, PHOTO"
              />
              <p className="text-[11px] text-muted-foreground">
                Shown as the second column in the doc. Conventionally ALL CAPS.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={includeInSummary}
                  onChange={(e) => setIncludeInSummary(e.target.checked)}
                />
                Include in timeline summary
              </Label>
              <p className="text-[11px] text-muted-foreground">
                Vendor load-in / break-down rows are usually unchecked.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="step-start">Start</Label>
              <Input
                id="step-start"
                type="datetime-local"
                value={startWall}
                onChange={(e) => setStartWall(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="step-end" className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={hasEndTime}
                  onChange={(e) => setHasEndTime(e.target.checked)}
                />
                End time
              </Label>
              <Input
                id="step-end"
                type="datetime-local"
                value={endWall}
                onChange={(e) => setEndWall(e.target.value)}
                disabled={!hasEndTime}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Body</Label>
            <div className="rounded-md border border-input bg-background px-3 py-2 min-h-[6rem]">
              <RichBody
                key={`step-${step?.id ?? 'new'}`}
                editable
                value={bodyJson}
                placeholder="Add details — checklist, table, mention vendors with @"
                onChange={(json, text) => {
                  setBodyJson(json);
                  setBodyText(text);
                }}
                mentionables={mentionables}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Participants</Label>
              <div className="flex gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setParticipants((arr) => [
                      ...arr,
                      { key: `new-${Date.now()}`, kind: 'vendor' },
                    ])
                  }
                >
                  + Vendor
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setParticipants((arr) => [
                      ...arr,
                      { key: `new-${Date.now()}`, kind: 'person', personKey: 'person1' },
                    ])
                  }
                >
                  + Person
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setParticipants((arr) => [
                      ...arr,
                      { key: `new-${Date.now()}`, kind: 'free' },
                    ])
                  }
                >
                  + Other
                </Button>
              </div>
            </div>

            {participants.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                No participants yet. Add vendors, people, or free-form labels above.
              </p>
            ) : (
              <div className="space-y-2">
                {participants.map((p, idx) => (
                  <div key={p.key} className="flex items-center gap-2 rounded-md border border-border p-2">
                    {p.kind === 'vendor' && (
                      <Select
                        value={p.projectVendorId ?? ''}
                        onValueChange={(v) =>
                          setParticipants((arr) =>
                            arr.map((x, i) => (i === idx ? { ...x, projectVendorId: v } : x)),
                          )
                        }
                        disabled={!!p.id}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Pick a project vendor" />
                        </SelectTrigger>
                        <SelectContent>
                          {(pvOptions ?? []).map((pv: ParticipantOption) => (
                            <SelectItem key={pv.id} value={pv.id}>
                              {pv.vendor.name}
                              {pv.role ? ` · ${pv.role}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {p.kind === 'person' && (
                      <Select
                        value={p.personKey ?? 'person1'}
                        onValueChange={(v) =>
                          setParticipants((arr) =>
                            arr.map((x, i) =>
                              i === idx ? { ...x, personKey: v as PersonKey } : x,
                            ),
                          )
                        }
                        disabled={!!p.id}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PERSON_KEYS.map((k) => (
                            <SelectItem key={k} value={k}>
                              {PERSON_KEY_LABELS[k]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {p.kind === 'free' && (
                      <Input
                        className="flex-1"
                        placeholder="e.g. Officiant"
                        value={p.freeText ?? ''}
                        onChange={(e) =>
                          setParticipants((arr) =>
                            arr.map((x, i) => (i === idx ? { ...x, freeText: e.target.value } : x)),
                          )
                        }
                        disabled={!!p.id}
                      />
                    )}
                    <Input
                      className="w-32"
                      placeholder="Role"
                      value={p.role ?? ''}
                      onChange={(e) =>
                        setParticipants((arr) =>
                          arr.map((x, i) => (i === idx ? { ...x, role: e.target.value } : x)),
                        )
                      }
                      disabled={!!p.id}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        setParticipants((arr) => arr.filter((_, i) => i !== idx))
                      }
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ))}
                {participants.some((p) => p.id) && (
                  <p className="text-[11px] text-muted-foreground italic">
                    Existing participants are read-only — remove and re-add to change.
                  </p>
                )}
              </div>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter className="justify-between sm:justify-between">
          {isEdit ? (
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => {
                if (confirm('Delete this step?')) deleteStepMutation.mutate();
              }}
              disabled={deleteStepMutation.isPending}
            >
              Delete step
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save' : 'Add step'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── New event from template ─────────────────────────────────────────────────

type TemplateListItem = InferResponseType<typeof client.api['event-templates']['$get'], 200>[number];

export function NewFromTemplateDialog({
  projectId,
  venueName,
  open,
  onOpenChange,
}: {
  projectId: string;
  venueName: string | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<'pick' | 'configure'>('pick');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [startWall, setStartWall] = useState('');
  const [timezone, setTimezone] = useState(
    () => guessTimezoneFromVenue(venueName) ?? browserTimezone(),
  );
  const [location, setLocation] = useState('');
  const [mapping, setMapping] = useState<Record<string, { projectVendorId?: string; personKey?: PersonKey }>>({});
  const [error, setError] = useState<string | null>(null);

  const { data: templates } = useQuery({
    queryKey: ['event-templates'],
    queryFn: async () => {
      const res = await client.api['event-templates'].$get();
      if (!res.ok) throw new Error('Failed to load templates');
      return res.json();
    },
    enabled: open,
  });

  const { data: template } = useQuery({
    queryKey: ['event-template', selectedTemplateId],
    queryFn: async () => {
      const res = await client.api['event-templates'][':id'].$get({
        param: { id: selectedTemplateId! },
      });
      if (!res.ok) throw new Error('Failed to load template');
      return res.json();
    },
    enabled: !!selectedTemplateId && stage === 'configure',
  });

  // For mapping vendors: fetch this project's active vendors. Reusing the
  // same options-shape as the step dialog by hitting the project-vendors
  // endpoint via the existing `/api/projects/:id/vendors` route.
  const { data: pvOptions } = useQuery({
    queryKey: ['project-vendors', projectId],
    queryFn: async () => {
      const res = await client.api.projects[':projectId'].vendors.$get({
        param: { projectId },
      });
      if (!res.ok) throw new Error('Failed to load vendors');
      return res.json();
    },
    enabled: open && stage === 'configure',
  });

  useEffect(() => {
    if (open) {
      setStage('pick');
      setSelectedTemplateId(null);
      setName('');
      setStartWall('');
      setTimezone(guessTimezoneFromVenue(venueName) ?? browserTimezone());
      setLocation(venueName ?? '');
      setMapping({});
      setError(null);
    }
  }, [open, venueName]);

  // Pre-fill defaults when entering configure stage with a chosen template.
  useEffect(() => {
    if (stage === 'configure' && template) {
      setName((cur) => cur || template.name);
      // Pre-map person placeholders to their corresponding personKey.
      setMapping((cur) => {
        const next = { ...cur };
        for (const k of template.uniquePlaceholderKeys) {
          if (next[k]) continue;
          if (PERSON_KEYS.includes(k as PersonKey)) {
            next[k] = { personKey: k as PersonKey };
          }
        }
        return next;
      });
    }
  }, [stage, template]);

  const instantiateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTemplateId) throw new Error('Pick a template first');
      if (!name.trim()) throw new Error('Name is required');
      if (!startWall) throw new Error('Start time is required');
      const startAt = zonedWallClockToInstant(startWall, timezone);
      // Strip empty mappings before sending.
      const cleanMapping: Record<string, { projectVendorId?: string; personKey?: PersonKey }> = {};
      for (const [k, v] of Object.entries(mapping)) {
        if (v.projectVendorId || v.personKey) cleanMapping[k] = v;
      }
      const res = await client.api['event-templates'].instantiate.$post({
        json: {
          projectId,
          templateId: selectedTemplateId,
          name: name.trim(),
          startAt: startAt.toISOString(),
          timezone,
          location: location.trim() || null,
          participantMapping: cleanMapping,
        },
      });
      if (!res.ok) throw new Error('Failed to create event');
      return res.json();
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['events', projectId] });
      onOpenChange(false);
      const url = new URL(window.location.href);
      url.searchParams.set('event', created.id);
      window.history.pushState({}, '', url);
      window.dispatchEvent(new PopStateEvent('popstate'));
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {stage === 'pick' ? 'Choose a template' : 'Set up event'}
          </DialogTitle>
          <DialogDescription>
            {stage === 'pick'
              ? 'Start from a built-in template or one you saved.'
              : 'Pick a date/time, then map placeholders to your vendors and people.'}
          </DialogDescription>
        </DialogHeader>

        {stage === 'pick' && (
          <div className="space-y-4">
            {!templates ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : templates.length === 0 ? (
              <p className="text-sm text-muted-foreground">No templates available.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {templates.map((t: TemplateListItem) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setSelectedTemplateId(t.id);
                      setStage('configure');
                    }}
                    className="text-left rounded-2xl border border-border bg-card p-4 transition hover:border-foreground hover:shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-medium">{t.name}</h3>
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                          {t.description ?? '—'}
                        </p>
                      </div>
                      {t.isSystem && (
                        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                          System
                        </span>
                      )}
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground">
                      {t.stepCount} steps · ≈ {Math.round(t.defaultDurationMinutes / 60)} hr
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {stage === 'configure' && template && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="tmpl-name">Event name</Label>
              <Input id="tmpl-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="tmpl-start">Start</Label>
                <Input
                  id="tmpl-start"
                  type="datetime-local"
                  value={startWall}
                  onChange={(e) => setStartWall(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Timezone</Label>
                <TimezoneSelect value={timezone} onChange={setTimezone} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tmpl-loc">Location</Label>
              <Input id="tmpl-loc" value={location} onChange={(e) => setLocation(e.target.value)} />
            </div>

            <div className="space-y-2 pt-2">
              <Label>Map participants</Label>
              <p className="text-xs text-muted-foreground">
                Each placeholder in this template can map to a project vendor or a person.
                Leave empty to skip — you can fill it in later.
              </p>
              <div className="space-y-2">
                {template.uniquePlaceholderKeys.map((key) => {
                  const isPersonKey = PERSON_KEYS.includes(key as PersonKey);
                  const current = mapping[key] ?? {};
                  return (
                    <div key={key} className="grid grid-cols-[140px_1fr] items-center gap-3">
                      <span className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</span>
                      {isPersonKey ? (
                        <Select
                          value={current.personKey ?? key}
                          onValueChange={(v) =>
                            setMapping((m) => ({ ...m, [key]: { personKey: v as PersonKey } }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PERSON_KEYS.map((k) => (
                              <SelectItem key={k} value={k}>
                                {PERSON_KEY_LABELS[k]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Select
                          value={current.projectVendorId ?? '__skip'}
                          onValueChange={(v) =>
                            setMapping((m) => ({
                              ...m,
                              [key]: v === '__skip' ? {} : { projectVendorId: v },
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Pick a project vendor or skip" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__skip">— Skip for now —</SelectItem>
                            {(pvOptions ?? []).map((pv) => (
                              <SelectItem key={pv.id} value={pv.id}>
                                {pv.vendor.name}
                                {pv.role ? ` · ${pv.role}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        <DialogFooter className="justify-between sm:justify-between">
          {stage === 'configure' ? (
            <Button variant="ghost" onClick={() => setStage('pick')}>
              ← Back
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {stage === 'configure' && (
              <Button
                onClick={() => instantiateMutation.mutate()}
                disabled={instantiateMutation.isPending}
              >
                <Sparkles className="size-4" />
                {instantiateMutation.isPending ? 'Creating…' : 'Create event'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Save current event as user template ─────────────────────────────────────

export function SaveAsTemplateDialog({
  event,
  open,
  onOpenChange,
}: {
  event: { id: string; name: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(event.name);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(event.name);
      setDescription('');
      setError(null);
    }
  }, [open, event]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Name is required');
      const res = await client.api['event-templates'].$post({
        json: {
          fromEventId: event.id,
          name: name.trim(),
          description: description.trim() || null,
          kind: 'custom',
        },
      });
      if (!res.ok) throw new Error('Failed to save template');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-templates'] });
      onOpenChange(false);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Save as template</DialogTitle>
          <DialogDescription>
            Capture this event's steps and participant roles as a reusable template.
            Vendor and person assignments are genericized.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="tmpl-save-name">Template name</Label>
            <Input
              id="tmpl-save-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tmpl-save-desc">Description (optional)</Label>
            <Textarea
              id="tmpl-save-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            <Calendar className="size-4" />
            {saveMutation.isPending ? 'Saving…' : 'Save template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
